// ============================================================
//  src/pages/integrations/IntegrationsPage.js
//
//  User-level AI provider settings — connect OpenAI, Claude,
//  Gemini. Keys are stored encrypted on the server; only a
//  masked version is shown after saving.
//  Enhanced with smooth interactions and mobile optimizations.
// ============================================================
import React, { useEffect, useState, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { listAiIntegrations, saveAiIntegration, deleteAiIntegration } from '../../api/client';
import './IntegrationsPage.css';

// ── Provider catalogue ────────────────────────────────────────
const PROVIDERS = [
  {
    id:       'openai',
    name:     'OpenAI',
    logo:     '🟢',
    color:    '#10a37f',
    tagline:  'GPT-4o, GPT-4o mini, GPT-3.5 Turbo',
    keyHint:  'Starts with sk-',
    docsUrl:  'https://platform.openai.com/api-keys',
    models: [
      { value: 'gpt-4o-mini',   label: 'GPT-4o mini (fast, cheap)' },
      { value: 'gpt-4o',        label: 'GPT-4o (smart, balanced)' },
      { value: 'gpt-4-turbo',   label: 'GPT-4 Turbo (most capable)' },
      { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (legacy)' },
    ],
  },
  {
    id:       'claude',
    name:     'Anthropic Claude',
    logo:     '🟠',
    color:    '#d97706',
    tagline:  'Claude Haiku, Sonnet, Opus',
    keyHint:  'Starts with sk-ant-',
    docsUrl:  'https://console.anthropic.com/settings/keys',
    models: [
      { value: 'claude-haiku-4-5-20251001',  label: 'Claude Haiku (fastest)' },
      { value: 'claude-sonnet-4-6',           label: 'Claude Sonnet (balanced)' },
      { value: 'claude-opus-4-6',             label: 'Claude Opus (most capable)' },
    ],
  },
  {
    id:       'gemini',
    name:     'Google Gemini',
    logo:     '🔵',
    color:    '#4285f4',
    tagline:  'Gemini 1.5 Flash, Pro, Ultra',
    keyHint:  'Google AI Studio API key',
    docsUrl:  'https://aistudio.google.com/app/apikey',
    models: [
      { value: 'gemini-1.5-flash',   label: 'Gemini 1.5 Flash (fast, cheap)' },
      { value: 'gemini-1.5-pro',     label: 'Gemini 1.5 Pro (balanced)' },
      { value: 'gemini-1.0-pro',     label: 'Gemini 1.0 Pro (legacy)' },
    ],
  },
];

// ── Provider Card (enhanced with animations) ─────────────────
function ProviderCard({ provider, saved, onSave, onDelete }) {
  const [open,    setOpen]    = useState(false);
  const [apiKey,  setApiKey]  = useState('');
  const [model,   setModel]   = useState(provider.models[0].value);
  const [saving,  setSaving]  = useState(false);
  const [deleting,setDeleting]= useState(false);
  const [showKey, setShowKey] = useState(false);
  const formRef = useRef(null);

  const isConnected = !!saved;

  // Pre-select saved model when opening
  const handleOpen = () => {
    if (saved?.model) setModel(saved.model);
    setOpen(o => !o);
    setApiKey('');
    // Small delay to focus input after animation
    setTimeout(() => {
      if (!open && formRef.current) {
        const firstInput = formRef.current.querySelector('input');
        if (firstInput) firstInput.focus();
      }
    }, 100);
  };

  const handleSave = async () => {
    if (!apiKey.trim()) { 
      toast.error('Please enter your API key', { icon: '🔑' });
      return;
    }
    setSaving(true);
    try {
      await onSave(provider.id, apiKey.trim(), model);
      setOpen(false);
      setApiKey('');
      toast.success(`${provider.name} connected successfully!`, { icon: '🎉' });
    } catch (err) {
      // Error is already handled in parent
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`⚠️ Remove ${provider.name} integration?\n\nYour bots using this provider will stop working.`)) return;
    setDeleting(true);
    try { 
      await onDelete(provider.id);
      toast.success(`${provider.name} disconnected`, { icon: '🗑️' });
    } finally { 
      setDeleting(false);
    }
  };

  return (
    <div className={`int-card${isConnected ? ' int-card--connected' : ''}`}>
      {/* Header */}
      <div className="int-card-header">
        <div className="int-provider-info">
          <div className="int-provider-logo" style={{ background: provider.color + '15', color: provider.color }}>
            {provider.logo}
          </div>
          <div>
            <div className="int-provider-name">{provider.name}</div>
            <div className="int-provider-tagline">{provider.tagline}</div>
          </div>
        </div>

        <div className="int-card-actions">
          {isConnected && (
            <>
              <span className="int-badge-connected">✓ Connected</span>
              <button 
                className="btn btn-ghost btn-sm" 
                onClick={handleDelete} 
                disabled={deleting}
                aria-label={`Remove ${provider.name} integration`}
              >
                {deleting ? '⋯' : 'Remove'}
              </button>
            </>
          )}
          <button
            className={`btn btn-sm ${isConnected ? 'btn-outline' : 'btn-primary'}`}
            onClick={handleOpen}
            aria-expanded={open}
            aria-label={isConnected ? `Update ${provider.name} key` : `Connect ${provider.name}`}
          >
            {open ? 'Cancel' : (isConnected ? 'Update key' : 'Connect')}
          </button>
        </div>
      </div>

      {/* Connected info */}
      {isConnected && !open && (
        <div className="int-connected-info">
          <div className="int-info-row">
            <span className="int-info-label">API Key</span>
            <span className="int-info-val monospace">
              {showKey ? saved.keyMasked : '••••••••••••••••'}
              <button 
                className="btn-inline" 
                onClick={() => setShowKey(s => !s)}
                aria-label={showKey ? 'Hide API key' : 'Show API key'}
              >
                {showKey ? 'hide' : 'show'}
              </button>
            </span>
          </div>
          <div className="int-info-row">
            <span className="int-info-label">Model</span>
            <span className="int-info-val monospace">{saved.model || '(account default)'}</span>
          </div>
          <div className="int-info-row">
            <span className="int-info-label">Updated</span>
            <span className="int-info-val">{new Date(saved.updatedAt).toLocaleDateString(undefined, { 
              year: 'numeric', 
              month: 'short', 
              day: 'numeric' 
            })}</span>
          </div>
        </div>
      )}

      {/* Connect / update form */}
      {open && (
        <div className="int-form" ref={formRef}>
          <div className="int-form-field">
            <label>
              API Key 
              <a 
                href={provider.docsUrl} 
                target="_blank" 
                rel="noreferrer" 
                className="int-docs-link"
              >
                Get key ↗
              </a>
            </label>
            <input
              type="password"
              placeholder={provider.keyHint}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              className="input"
              autoComplete="off"
              spellCheck={false}
              aria-label={`${provider.name} API key`}
            />
          </div>
          <div className="int-form-field">
            <label>
              Default model 
              <span className="int-label-hint">override per command</span>
            </label>
            <select 
              value={model} 
              onChange={e => setModel(e.target.value)} 
              className="input"
              aria-label={`${provider.name} default model`}
            >
              {provider.models.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="int-form-actions">
            <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button 
              className="btn btn-primary btn-sm" 
              onClick={handleSave} 
              disabled={saving}
            >
              {saving ? 'Saving⋯' : (isConnected ? 'Update' : 'Save & Connect')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Usage docs snippet (enhanced copy feedback) ───────────────
function UsageDoc() {
  const [copied, setCopied] = useState(false);
  const snippet = JSON.stringify([
    {
      type:          "ai_reply",
      provider:      "openai",
      model:         "gpt-4o-mini",
      system_prompt: "You are a helpful WhatsApp assistant. Keep replies short.",
      memory:        true,
      max_tokens:    400,
    }
  ], null, 2);

  const copy = () => {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      toast.success('Copied to clipboard!', { icon: '📋', duration: 1500 });
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast.error('Failed to copy', { icon: '❌' });
    });
  };

  return (
    <div className="int-docs">
      <div className="int-docs-title">
        <span>📋</span> How to use AI in your bot commands
      </div>
      <p>
        After connecting a provider, add an <code>ai_reply</code> action to any command's action list.
        The system will use your saved API key automatically.
      </p>
      <div className="int-code-block">
        <button className="int-copy-btn" onClick={copy} aria-label="Copy code snippet">
          {copied ? '✓ Copied!' : 'Copy'}
        </button>
        <pre>{snippet}</pre>
      </div>
      <div className="int-docs-options">
        <div className="int-opt-row"><code>provider</code> <span>openai · claude · gemini</span></div>
        <div className="int-opt-row"><code>model</code> <span>Override the account default model for this command</span></div>
        <div className="int-opt-row"><code>system_prompt</code> <span>Personality / instructions for the AI</span></div>
        <div className="int-opt-row"><code>memory</code> <span>true = remember the conversation per user session</span></div>
        <div className="int-opt-row"><code>memory_trigger</code> <span>Shared memory key across multiple commands (optional)</span></div>
        <div className="int-opt-row"><code>max_tokens</code> <span>Max reply length (default: 500)</span></div>
        <div className="int-opt-row"><code>temperature</code> <span>0.0 = precise · 1.0 = creative (default: 0.7)</span></div>
        <div className="int-opt-row"><code>save_as</code> <span>Store reply in <code>{`{{ai.reply}}`}</code> for use in next actions</span></div>
      </div>
    </div>
  );
}

// ── Main page (with smooth loading states) ───────────────────
export default function IntegrationsPage() {
  const [integrations, setIntegrations] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listAiIntegrations();
      if (res.data?.success) {
        setIntegrations(res.data.integrations ?? []);
      } else {
        setError(res.data?.error || 'Failed to load integrations');
      }
    } catch (err) {
      console.error('Load integrations error:', err);
      setError('Network error. Please check your connection.');
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (provider, apiKey, model) => {
    try {
      const res = await saveAiIntegration(provider, apiKey, model);
      if (res.data?.success) {
        await load();
        return true;
      } else {
        const errorMsg = res.data?.error ?? 'Failed to save integration';
        toast.error(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error(err.message || 'Failed to save integration');
      throw err;
    }
  };

  const handleDelete = async (provider) => {
    try {
      const res = await deleteAiIntegration(provider);
      if (res.data?.success) {
        await load();
        return true;
      } else {
        toast.error(res.data?.error ?? 'Failed to remove integration');
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to remove integration');
    }
  };

  const savedMap = Object.fromEntries(integrations.map(i => [i.provider, i]));

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <div>
          <h1 className="page-title">AI Integrations</h1>
          <p className="page-sub">
            Connect your AI provider keys once — use them across all your bots.
            Keys are encrypted at rest.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="tab-loading">
          <span className="spinner spinner-dark" />
          <span>Loading integrations...</span>
        </div>
      ) : error ? (
        <div className="tab-loading" style={{ flexDirection: 'column', gap: '16px' }}>
          <span style={{ fontSize: '32px' }}>⚠️</span>
          <span style={{ color: 'var(--int-danger)' }}>{error}</span>
          <button className="btn btn-outline btn-sm" onClick={load}>
            Try Again
          </button>
        </div>
      ) : (
        <>
          <div className="int-grid">
            {PROVIDERS.map(p => (
              <ProviderCard
                key={p.id}
                provider={p}
                saved={savedMap[p.id] ?? null}
                onSave={handleSave}
                onDelete={handleDelete}
              />
            ))}
          </div>
          <UsageDoc />
        </>
      )}
    </div>
  );
}